import type { ManagedCall } from '@repo/sdk/gen'
import { Badge } from '@repo/ui/badge'
import { makeBreadcrumbFromCache } from '@repo/ui/components/query-breadcrumbs'
import { useQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	Link,
	notFound,
	Outlet,
	retainSearchParams
} from '@tanstack/react-router'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { CallStateBadge } from '@/routes/(private)/-components/call-state-badge'
import { YearSelector } from '../-components/year-selector'
import { useWorkingCall } from '../-hooks/use-working-call'
import { workingCallOf } from '../-lib/working-call'
import {
	managedCallQueryOptions,
	managedCallsQueryOptions,
	managerHomeQueryOptions
} from '../-query/query-options'

export const Route = createFileRoute('/(private)/bando/$residenceId')({
	validateSearch: z.object({
		/** The academic year of the call shown, `2026-2027`; the current call without it. */
		anno: z
			.string()
			.regex(/^\d{4}-\d{4}$/)
			.optional()
			.catch(undefined)
	}),
	// The year stays in the address on every page of the residence.
	search: { middlewares: [retainSearchParams(['anno'])] },
	loaderDeps: ({ search }) => ({ anno: search.anno }),
	loader: async ({ context, params, deps }) => {
		try {
			// Show what is cached at once, and read the call again if it is stale.
			const list = await context.queryClient.ensureQueryData({
				...managedCallsQueryOptions(),
				revalidateIfStale: true
			})
			const { callId } = workingCallOf(
				list,
				params.residenceId,
				deps.anno
			)
			// A year the residence has no call for is a page that does not exist.
			if (deps.anno && !callId) {
				throw notFound()
			}
			await context.queryClient.ensureQueryData({
				...managedCallQueryOptions(params.residenceId, callId),
				revalidateIfStale: true
			})
		} catch (error) {
			// Another manager's residence answers as if it did not exist.
			if (
				typeof error === 'object' &&
				error !== null &&
				'code' in error &&
				error.code === 'residence_not_found'
			) {
				throw notFound()
			}
			throw error
		}
	},
	staticData: {
		breadcrumb: ({ params, queryClient }) => {
			const residenceId = params.residenceId ?? ''
			// The loader stores the call of the academic year in the address under that
			// call's id, not under "current": the crumb reads the call of this residence
			// that was read last.
			const [latest] = queryClient
				.getQueryCache()
				.findAll({
					queryKey: makeQueryKeys.call
						.managed(residenceId)
						.slice(0, 3),
					predicate: (query) =>
						query.queryKey[1] === 'managed' &&
						query.queryKey[2] === residenceId &&
						query.queryKey.length === 4
				})
				.sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
			return makeBreadcrumbFromCache<ManagedCall>(
				queryClient,
				latest?.queryKey ?? makeQueryKeys.call.managed(residenceId),
				(myCall) => myCall.residence.name,
				(myCall) => <CallStateBadge state={myCall.state} />
			)
		}
	},
	component: ResidenceLayout
})

/** The pages of one residence, as tabs above the page; a manager of several residences has no other way between them. */
function ResidenceLayout() {
	const { t } = useTranslation()
	const { residenceId } = Route.useParams()
	const { callId, state } = useWorkingCall()
	// The draft of the next year has no applications, ranking or offers yet: only its call is shown.
	const draft = state === 'draft'
	// The flags still to close and the verifications still pending, beside their tabs: the home holds no student.
	const counters = useQuery(managerHomeQueryOptions(residenceId, callId)).data
		?.counters
	const openFlags = counters?.openFlags ?? null
	const pendingVerifications = counters?.pendingVerifications ?? null
	const tabClass =
		'border-transparent border-b-2 px-1 py-2 text-muted-foreground text-sm hover:text-foreground data-[status=active]:border-primary data-[status=active]:font-medium data-[status=active]:text-foreground'
	return (
		<div className='flex min-h-0 flex-1 flex-col'>
			<nav
				aria-label={t('admin-call:activity.nav.label')}
				className='flex shrink-0 gap-6 overflow-x-auto whitespace-nowrap border-border border-b px-4 md:px-6'
			>
				{!draft && (
					<Link
						activeOptions={{ exact: true }}
						className={tabClass}
						params={{ residenceId }}
						to='/bando/$residenceId/attivita'
					>
						{t('admin-call:activity.nav.activity')}
					</Link>
				)}
				<Link
					activeOptions={{ exact: true }}
					className={tabClass}
					params={{ residenceId }}
					to='/bando/$residenceId'
				>
					{t('admin-call:activity.nav.call')}
				</Link>
				{!draft && (
					<Link
						className={tabClass}
						params={{ residenceId }}
						to='/bando/$residenceId/domande'
					>
						{t('admin-call:activity.nav.applications')}
					</Link>
				)}
				{!draft && (
					<Link
						className={tabClass}
						params={{ residenceId }}
						to='/bando/$residenceId/graduatoria'
					>
						{t('admin-call:activity.nav.ranking')}
					</Link>
				)}
				{!draft && (
					<Link
						className={tabClass}
						params={{ residenceId }}
						to='/bando/$residenceId/offerte'
					>
						{t('admin-call:activity.nav.offers')}
					</Link>
				)}
				{!draft && (
					<Link
						className={`${tabClass} flex items-center gap-2`}
						params={{ residenceId }}
						to='/bando/$residenceId/ingressi'
					>
						{t('admin-call:activity.nav.arrivals')}
						{pendingVerifications !== null && (
							<Badge
								aria-label={t(
									'admin-call:activity.nav.verifications_pending',
									{ count: pendingVerifications }
								)}
								variant='neutral'
							>
								{pendingVerifications}
							</Badge>
						)}
					</Link>
				)}
				{!draft && (
					<Link
						className={`${tabClass} flex items-center gap-2`}
						params={{ residenceId }}
						to='/bando/$residenceId/segnalazioni'
					>
						{t('admin-call:activity.nav.flags')}
						{openFlags !== null && (
							<Badge
								aria-label={t(
									'admin-call:activity.nav.flags_open',
									{
										count: openFlags
									}
								)}
								variant='neutral'
							>
								{openFlags}
							</Badge>
						)}
					</Link>
				)}
			</nav>
			<YearSelector />
			{/* The pages scroll here, under the tabs: one that does not scroll by itself would be cut at the height of the window. */}
			<div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
				<Outlet />
			</div>
		</div>
	)
}
